import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { MedplumProvider } from '@medplum/react-hooks';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Toaster } from 'sonner';
import { SidebarProvider } from '../../contexts/SidebarContext';
import { ThemeProvider, useTheme } from '../../contexts/ThemeContext';
import { medplum } from '../../medplum';

/**
 * Root provider tree (router-agnostic so tests can mount it under a MemoryRouter).
 *
 * Order matters:
 *  - QueryClientProvider — app-level data caching (@tanstack/react-query).
 *  - MedplumProvider — the FHIR client + auth/profile context.
 *  - ThemeProvider — ACTO light/dark; wraps Mantine so Mantine can follow it.
 *  - MantineProvider — the ENGINE behind `@medplum/react` wrappers ONLY (chrome
 *    itself is ACTO/Tailwind; Mantine never styles our own components).
 *  - SidebarProvider — ACTO chrome state.
 */
const queryClient = new QueryClient();

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <MedplumProvider medplum={medplum}>
        <ThemeProvider>
          <MantineThemed>
            <Notifications position="bottom-right" />
            <SidebarProvider>
              {children}
              <Toaster position="top-right" richColors closeButton />
            </SidebarProvider>
          </MantineThemed>
        </ThemeProvider>
      </MedplumProvider>
    </QueryClientProvider>
  );
}

/**
 * Mantine follows the ACTO light/dark toggle. Otherwise Mantine stays light (white Paper) while
 * `body` text goes light in dark mode — e.g. the sign-in form's labels vanish on a white card.
 */
function MantineThemed({ children }: { children: ReactNode }) {
  const { resolvedTheme } = useTheme();
  return <MantineProvider forceColorScheme={resolvedTheme}>{children}</MantineProvider>;
}
